import React from 'react';

import sanitizeHtml from 'sanitize-html';

export const nodeToString = (node: React.ReactNode): string => {
  // 기존 코드 유지 (변경 없음)
  if (node === null || node === undefined) {
    return '';
  }

  if (typeof node === 'boolean') {
    return '';
  }

  if (typeof node === 'number' || typeof node === 'string') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(nodeToString).join('');
  }

  // React 요소나 객체인 경우
  if (typeof node === 'object') {
    // React Fragment인 경우 (children이 있는지 확인)
    if (
      React.isValidElement(node) &&
      'props' in node &&
      node.props &&
      'children' in (node.props as unknown as { children: React.ReactNode })
    ) {
      return nodeToString(
        (node.props as unknown as { children: React.ReactNode }).children
      );
    }
    // toString 메서드가 있는 경우
    if (
      node &&
      typeof node === 'object' &&
      'toString' in node &&
      node.toString !== Object.prototype.toString
    ) {
      return node.toString();
    }
  }

  // 그 외 처리할 수 없는 타입
  return '';
};

export const sanitizeContent = (content: string) => {
  // content가 없으면 빈 문자열 반환
  if (!content || content === '') return content;

  try {
    // sanitize-html을 사용하여 정제
    const sanitizeOptions = {
      // DOMPurify의 기본 허용 태그들을 포함시키기
      allowedTags: [
        ...sanitizeHtml.defaults.allowedTags,
        'svg',
        'path',
        'circle',
        'rect',
        'line',
        'polyline',
        'polygon',
        'ellipse',
        'math',
        'mi',
        'mn',
        'mo',
        'ms',
        'mtext',
        'mrow',
        'mfrac',
        'msqrt',
        'msub',
        'msup',
      ],

      // 금지할 태그 직접 처리 (sanitize-html에서는 disallowedTags 옵션 대신 직접 필터링 사용)
      exclusiveFilter: (frame: { tag: string }) =>
        frame.tag === 'script' || frame.tag === 'iframe',

      // 속성 허용 설정
      allowedAttributes: {
        ...sanitizeHtml.defaults.allowedAttributes,
        '*': [
          'id',
          'class',
          'style',
          'xmlns',
          'viewBox',
          'd',
          'cx',
          'cy',
          'r',
          'x',
          'y',
          'width',
          'height',
          'fill',
          'stroke',
          'data-*',
          'aria-*',
        ],
      },

      // 금지할 속성 설정
      disallowedAttributes: {
        '*': [
          'onerror',
          'onload',
          'onclick',
          'onmouseover',
          'onmouseout',
          'onunload',
          'onabort',
        ],
      },

      // href나 src 같은 URL 속성에 대한 프로토콜 제한
      allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],

      // 안전하지 않은 CSS 속성 필터링
      allowedStyles: {
        '*': {
          color: [/.*/],
          'background-color': [/.*/],
          'font-size': [/.*/],
          'text-align': [/.*/],
          margin: [/.*/],
          padding: [/.*/],
          border: [/.*/],
          // 필요한 다른 CSS 속성들 추가
        },
      },
    };

    return sanitizeHtml(content, sanitizeOptions);
  } catch (error) {
    console.error('Sanitization failed:', error);
    return content;
  }
};
